/**
 * @description
 * Custom hook for managing InvoiceManagement page data and state.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { toast } from 'react-hot-toast';
import type { InvoiceManagementState } from '../types';

export function useInvoiceData() {
  const [state, setState] = useState<InvoiceManagementState>({
    invoices: [],
    clients: [],
    subscriptions: [],
    loading: true,
    searchTerm: '',
    statusFilter: 'all',
    typeFilter: 'all',
    clientFilter: 'all',
    showCreateModal: false,
    selectedInvoices: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const [invoicesData, clientsData] = await Promise.all([
        api.getInvoices({ limit: 100 }),
        api.getClients()
      ]);
      
      // Get subscriptions for all clients
      const subscriptionsData = [];
      for (const client of clientsData) {
        try {
          const clientSubscriptions = await api.getSubscriptions(client.id);
          subscriptionsData.push(...clientSubscriptions);
        } catch (error) {
          console.warn(`Failed to load subscriptions for client ${client.id}:`, error);
        }
      }
      
      setState(prev => ({
        ...prev,
        invoices: invoicesData,
        clients: clientsData,
        subscriptions: subscriptionsData,
      }));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load invoice data');
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const filteredInvoices = state.invoices.filter(invoice => {
    const matchesSearch = invoice.clientName.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
                         invoice.id.toLowerCase().includes(state.searchTerm.toLowerCase());
    const matchesStatus = state.statusFilter === 'all' || invoice.status === state.statusFilter;
    const matchesType = state.typeFilter === 'all' || invoice.invoiceType === state.typeFilter;
    const matchesClient = state.clientFilter === 'all' || invoice.clientId === state.clientFilter;
    
    return matchesSearch && matchesStatus && matchesType && matchesClient;
  });

  const handleBulkAction = async (action: string) => {
    if (state.selectedInvoices.length === 0) {
      toast.error('Please select invoices first');
      return;
    }

    try {
      switch (action) {
        case 'download':
          toast.success(`Downloading ${state.selectedInvoices.length} invoices...`);
          break;
        case 'mark_paid':
          await api.bulkUpdateInvoices(state.selectedInvoices, { status: 'paid' });
          toast.success(`Marked ${state.selectedInvoices.length} invoices as paid`);
          break;
        case 'void':
          await api.bulkUpdateInvoices(state.selectedInvoices, { status: 'void' });
          toast.success(`Voided ${state.selectedInvoices.length} invoices`);
          break;
      }
      setState(prev => ({ ...prev, selectedInvoices: [] }));
      await loadData();
    } catch (error) {
      console.error('Error performing bulk action:', error);
      toast.error('Failed to perform bulk action');
    }
  };

  const handleSelectInvoice = (invoiceId: string) => {
    setState(prev => ({
      ...prev,
      selectedInvoices: prev.selectedInvoices.includes(invoiceId) 
        ? prev.selectedInvoices.filter(id => id !== invoiceId)
        : [...prev.selectedInvoices, invoiceId]
    }));
  };

  const handleSelectAll = () => {
    setState(prev => ({
      ...prev,
      selectedInvoices: prev.selectedInvoices.length === filteredInvoices.length
        ? []
        : filteredInvoices.map(invoice => invoice.id)
    }));
  };

  const setSearchTerm = (term: string) => {
    setState(prev => ({ ...prev, searchTerm: term }));
  };

  const setStatusFilter = (status: string) => {
    setState(prev => ({ ...prev, statusFilter: status }));
  };

  const setTypeFilter = (type: string) => {
    setState(prev => ({ ...prev, typeFilter: type }));
  };

  const setClientFilter = (client: string) => {
    setState(prev => ({ ...prev, clientFilter: client }));
  };

  const setShowCreateModal = (show: boolean) => {
    setState(prev => ({ ...prev, showCreateModal: show }));
  };

  return {
    // State
    ...state,
    filteredInvoices,
    
    // Actions
    setSearchTerm,
    setStatusFilter,
    setTypeFilter,
    setClientFilter,
    setShowCreateModal,
    handleBulkAction,
    handleSelectInvoice,
    handleSelectAll,
    loadData,
  };
}
